import ApiService from './ApiService'
import endpointConfig from '@/configs/endpoint.config'
import type {
    AddOnPurchase,
    BillingSummary,
    Invoice,
    PaddleCheckout,
    PaymentMethodSummary,
    PlanFeaturesResponse,
    PlanOption,
    SubscribeResult,
} from '@/@types/trackheros'

/**
 * Billing endpoints. All user-scoped — the server derives everything from the
 * authenticated user, and nothing here takes a client id.
 */

export async function apiGetBillingSummary() {
    return ApiService.fetchDataWithAxios<BillingSummary>({
        url: endpointConfig.billingSummary,
        method: 'get',
    })
}

export async function apiGetPlans() {
    return ApiService.fetchDataWithAxios<{ plans: PlanOption[] }>({
        url: endpointConfig.billingPlans,
        method: 'get',
    })
}

/**
 * Under Paddle a paid plan with no subscription behind it answers
 * `{checkout}` — open it with `openPaddleCheckout`; the plan arrives by
 * webhook once Paddle has the payment.
 */
export async function apiSubscribe(planCode: string) {
    return ApiService.fetchDataWithAxios<
        SubscribeResult,
        { plan_code: string }
    >({
        url: endpointConfig.billingSubscribe,
        method: 'post',
        data: { plan_code: planCode },
    })
}

/**
 * Stripe: a hosted Checkout URL to redirect to. Paddle: a transaction that
 * replaces the card on the live subscription, to open in Paddle.js.
 *
 * Either way the result is a UX cue only: the card is on file when the
 * provider's webhook says so, so the page revalidates rather than assuming.
 */
export async function apiStartPaymentSetup() {
    return ApiService.fetchDataWithAxios<
        { checkout_url: string } | { checkout: PaddleCheckout }
    >({
        url: endpointConfig.billingPaymentSetup,
        method: 'post',
    })
}

export async function apiGetPaymentMethod() {
    return ApiService.fetchDataWithAxios<{
        payment_method: PaymentMethodSummary | null
    }>({
        url: endpointConfig.billingPaymentMethod,
        method: 'get',
    })
}

export async function apiRemovePaymentMethod() {
    return ApiService.fetchDataWithAxios<void>({
        url: endpointConfig.billingPaymentMethod,
        method: 'delete',
    })
}

export async function apiGetInvoices() {
    return ApiService.fetchDataWithAxios<{ invoices: Invoice[] }>({
        url: endpointConfig.billingInvoices,
        method: 'get',
    })
}

/** Paddle's customer portal: cancel, change payment method, see receipts. */
export async function apiOpenBillingPortal() {
    return ApiService.fetchDataWithAxios<{
        url: string
        cancel_url: string
        update_payment_method_url: string
    }>({
        url: endpointConfig.billingPortal,
        method: 'post',
    })
}

/** A short-lived PDF link for one Paddle invoice. Ownership is re-checked server-side. */
export async function apiGetInvoiceDownload(invoiceId: string) {
    return ApiService.fetchDataWithAxios<{ url: string }>({
        url: endpointConfig.billingInvoiceDownload(invoiceId),
        method: 'get',
    })
}

/** What the plan includes — readable by any member, unlike the summary. */
export async function apiGetPlanFeatures() {
    return ApiService.fetchDataWithAxios<PlanFeaturesResponse>({
        url: endpointConfig.billingFeatures,
        method: 'get',
    })
}

/** "Make room when full": admins only. */
export async function apiSetAutoFreeStorage(enabled: boolean) {
    return ApiService.fetchDataWithAxios<
        { auto_free_storage: boolean },
        { auto_free_storage: boolean }
    >({
        url: endpointConfig.billingStorageSettings,
        method: 'patch',
        data: { auto_free_storage: enabled },
    })
}

/**
 * Buy 20 meeting hours for this month. `requestId` is one per button press —
 * sending it again returns the first purchase rather than charging twice.
 * 201 paid, 202 accepted and confirming, 402 declined.
 */
export async function apiBuyHours(requestId: string) {
    return ApiService.fetchDataWithAxios<
        { purchase: AddOnPurchase },
        { request_id: string }
    >({
        url: endpointConfig.billingAddonHours,
        method: 'post',
        data: { request_id: requestId },
    })
}

/** Set the number of storage packs on the subscription. Prorated by Paddle. */
export async function apiSetStoragePacks(packs: number) {
    return ApiService.fetchDataWithAxios<{ packs: number }, { packs: number }>({
        url: endpointConfig.billingAddonStorage,
        method: 'put',
        data: { packs },
    })
}
